import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Palmtree, LogIn, LogOut, Menu, X, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate('/');
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const hideNavbar = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);
  if (hideNavbar) return null;

  return (
    <header className="py-3 px-4 md:py-4 md:px-12 border-b border-stone-200 bg-white/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => go('/')}
          data-testid="navbar-logo"
        >
          <Palmtree className="w-6 h-6 md:w-7 md:h-7 text-[#005F73]" strokeWidth={1.5} />
          <span className="text-lg md:text-xl font-semibold text-[#005F73]">GwadImmo Invest</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-3">
          <Button variant="ghost" onClick={() => go('/pricing')} className="text-[#005F73] hover:text-[#005F73]/80" data-testid="navbar-pricing">
            Tarifs
          </Button>
          {isAuthenticated ? (
            <>
              <Button variant="ghost" onClick={() => go('/projects')} className="text-[#005F73] hover:text-[#005F73]/80" data-testid="navbar-projects">
                Mes Projets
              </Button>
              {user?.plan === 'professional' && (
                <Button variant="ghost" onClick={() => go('/compare')} className="text-[#005F73] hover:text-[#005F73]/80" data-testid="navbar-compare">
                  Comparer
                </Button>
              )}
              {user?.is_admin && (
                <Button variant="ghost" onClick={() => go('/admin')} className="text-[#EE9B00] hover:text-[#EE9B00]/80 font-medium" data-testid="navbar-admin">
                  Admin
                </Button>
              )}
              <Button onClick={() => go('/calculator')} className="rounded-full px-6 bg-[#005F73] hover:bg-[#005F73]/90 text-white" data-testid="navbar-calculator">
                Tableau de bord
              </Button>
              <Button variant="ghost" onClick={() => go('/account')} className="text-[#005F73] hover:text-[#005F73]/80" data-testid="navbar-account">
                <UserCircle className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Mon compte
              </Button>
              <Button data-testid="navbar-logout" variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => go('/login')} className="text-[#005F73] hover:text-[#005F73]/80" data-testid="navbar-login">
                <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Connexion
              </Button>
              <Button onClick={() => go('/register')} className="rounded-full px-6 bg-[#005F73] hover:bg-[#005F73]/90 text-white" data-testid="navbar-register">
                S'inscrire
              </Button>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 text-[#005F73]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="navbar-mobile-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden mt-3 pb-3 border-t border-stone-200 pt-3 flex flex-col gap-2" data-testid="navbar-mobile-menu">
          <Button variant="ghost" onClick={() => go('/pricing')} className="justify-start text-[#005F73] w-full">
            Tarifs
          </Button>
          {isAuthenticated ? (
            <>
              <Button variant="ghost" onClick={() => go('/projects')} className="justify-start text-[#005F73] w-full">
                Mes Projets
              </Button>
              {user?.plan === 'professional' && (
                <Button variant="ghost" onClick={() => go('/compare')} className="justify-start text-[#005F73] w-full">
                  Comparer
                </Button>
              )}
              <Button onClick={() => go('/calculator')} className="justify-start rounded-lg bg-[#005F73] hover:bg-[#005F73]/90 text-white w-full">
                Tableau de bord
              </Button>
              <Button variant="ghost" onClick={() => go('/account')} className="justify-start text-[#005F73] w-full" data-testid="navbar-mobile-account">
                <UserCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Mon compte
              </Button>
              {user?.is_admin && (
                <Button variant="ghost" onClick={() => go('/admin')} className="justify-start text-[#EE9B00] font-medium w-full">
                  Admin
                </Button>
              )}
              <Button variant="ghost" onClick={handleLogout} className="justify-start text-red-600 hover:bg-red-50 w-full" data-testid="navbar-mobile-logout">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => go('/login')} className="justify-start text-[#005F73] w-full">
                <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Connexion
              </Button>
              <Button onClick={() => go('/register')} className="rounded-lg bg-[#005F73] hover:bg-[#005F73]/90 text-white w-full">
                S'inscrire
              </Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
